import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { GlobalContext } from "../../../../global/context/Provider";
import isAuthenticated from "../../../../global/utils/isAuthenticated";
import WebChatWidget, { getWebChatConfig } from "./WebChatWidget";

/**
 * Unica instancia del chat interno a nivel app.
 * Los puntos de entrada (pestaña del LoginBox, boton flotante logueado) usan useWebChat()
 * para abrir/cerrar y mostrar no leidos; el iframe es siempre el mismo, asi la conversacion
 * sigue al pasar del login al portal logueado.
 */
const WebChatContext = createContext({
  enabled: false,
  open: false,
  unread: 0,
  openChat: () => {},
  closeChat: () => {},
  toggleChat: () => {},
});

export const useWebChat = () => useContext(WebChatContext);

const leerOpcBot = () => {
  try {
    const config = JSON.parse(localStorage.getItem("config") || "{}");
    return config?.opcBot !== false;
  } catch (_e) {
    return false;
  }
};

export const WebChatProvider = ({ children }) => {
  const { authState } = useContext(GlobalContext);
  const webchat = useMemo(() => getWebChatConfig(), []);
  const enabled = useMemo(() => !!webchat && leerOpcBot(), [webchat]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Se reevalua cuando cambia el estado de auth (login, logout, reset de contexto).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const authenticated = useMemo(() => isAuthenticated(), [authState]);

  const openChat = useCallback(() => setOpen(true), []);
  const closeChat = useCallback(() => setOpen(false), []);
  const toggleChat = useCallback(() => setOpen((abierto) => !abierto), []);

  const value = useMemo(
    () => ({ enabled, open, unread, openChat, closeChat, toggleChat }),
    [enabled, open, unread, openChat, closeChat, toggleChat]
  );

  return (
    <WebChatContext.Provider value={value}>
      {children}
      {enabled && (
        <WebChatWidget
          open={open}
          onClose={closeChat}
          onUnread={setUnread}
          webchat={webchat}
          authenticated={authenticated}
          raised={authenticated}
        />
      )}
    </WebChatContext.Provider>
  );
};

export default WebChatProvider;
